import * as React from 'react';
import {useTranslation} from 'react-i18next';

const Body: React.FC = () => {
  const {i18n} = useTranslation();
  return <div>{i18n.language === 'es-mx' ? <Spanish /> : <English />}</div>;
};

const English: React.FC = () => (
  <>
    <h2>Who can use this form?</h2>
    <p>People with Medicare who want to join a Medicare Advantage Plan. </p>
    <h2>To join a plan, you must:</h2>
    <ul>
      <li>Be a United States citizen or be lawfully present in the U.S.</li>
      <li>Live in the plan's service area</li>
    </ul>
    <h2>
      IMPORTANT: To join a Medicare Advantage Plan, you must also have both:
    </h2>
    <ul>
      <li>Medicare Part A (Hospital Insurance)</li>
      <li>Medicare Part B (Medical Insurance)</li>
    </ul>
    <p>
      To join a Dual-Eligible Special Needs Plan (D-SNP), you must qualify for
      both Medicare and Medicaid. To join a Chronic Condition Special Needs Plan
      (C-SNP), you must have a qualifying chronic condition.{' '}
    </p>
    <h2>When do I use this form?</h2>
    <p>You can join a plan:</p>
    <ul>
      <li>
        Between October 15-December 7 each year (for coverage starting January
        1)
      </li>
      <li>Within 3 months of first getting Medicare</li>
      <li>
        In certain situations where you're allowed to join or switch plans
      </li>
    </ul>
    <p>
      Visit <a href="http://medicare.gov">Medicare.gov</a> to learn more about
      when you can sign up for a plan.
    </p>
    <h2>What do I need to complete this form?</h2>
    <ul>
      <li>
        Your Medicare Number (the number on your red, white, and blue Medicare
        card)
      </li>
      <li>Your permanent address and phone number</li>
    </ul>
    <p>
      <strong>Note:</strong> You must complete all items in Step 1 (Personal).
      The items in Step 3 (Options) are optional — you can't be denied coverage
      because you don't fill them out.
    </p>
    <h2>Reminders</h2>
    <ul>
      <li>
        If you want to join a plan during fall open enrollment (October
        15-December 7), the plan must get your completed form by December 7.
      </li>
      <li>
        If you need to pay a plan premium, your plan will send you a bill. You
        can choose to sign up to have your premium payments deducted from your
        monthly Social Security (or Railroad Retirement Board) benefit.
      </li>
    </ul>
    <h2>What happens next?</h2>
    <p>Once we process your request to join, we'll contact you.</p>
    <h2>How do I get help with this form?</h2>
    <p>
      Call Devoted Health at <a href="tel:1-800-990-0723">1-800-990-0723</a>{' '}
      (TTY 711). Or, call Medicare at 1-800-MEDICARE (1-800-633-4227). TTY users
      can call 1-877-486-2048.
    </p>
    <h2>Individuals experiencing homelessness:</h2>
    <p>
      If you want to join a plan but have no permanent residence, a Post Office
      Box, an address of a shelter or clinic, or the address where you receive
      mail (e.g., Social Security checks) may be considered your permanent
      residence address.
    </p>
  </>
);

const Spanish: React.FC = () => (
  <>
    <h2>¿Quiénes pueden llenar este formulario?</h2>
    <p>
      Las personas que tienen Medicare y desean unirse a un Plan Medicare
      Advantage.
    </p>
    <h2>Para unirse a un plan, debe:</h2>
    <ul>
      <li>
        Ser ciudadano de los Estados Unidos o residir legalmente en este país
      </li>
      <li>Vivir en el área de servicio del plan.</li>
    </ul>
    <h2>
      Importante: Para unirse a un plan Medicare Advantage, también debe tener
      estos dos seguros:
    </h2>
    <ul>
      <li>Parte A de Medicare (seguro hospitalario)</li>
      <li>Parte B de Medicare (seguro médico)</li>
    </ul>
    <p>
      Para inscribirse en un Plan de Necesidades especiales de doble
      elegibilidad (Dual-Eligible Special Needs Plan, D-SNP), debe reunir los
      requisitos para Medicare y Medicaid. Para inscribirse en un Plan de
      Necesidades especiales para afecciones crónicas (Chronic Condition Special
      Needs Plan, C-SNP), debe tener una afección crónica que califique.{' '}
    </p>
    <h2>¿Cuándo puedo llenar este formulario?</h2>
    <p>Usted puede unirse a un plan:</p>
    <ul>
      <li>
        Entre el 15 de octubre y el 7 de diciembre de cada año (para que la
        cobertura comience el 1 de enero)
      </li>
      <li>
        Dentro de los 3 meses siguientes al inicio de la cobertura de Medicare
        por primera vez
      </li>
      <li>
        En ciertas situaciones en que tenga permitido inscribirse o cambiar de
        plan
      </li>
    </ul>
    <p>
      Visite <a href="http://medicare.gov">Medicare.gov</a> para obtener más
      información sobre cuándo puede inscribirse en un plan.{' '}
    </p>
    <h2>¿Qué información necesito para llenar este formulario?</h2>
    <ul>
      <li>
        Su número de Medicare (el número que aparece en su tarjeta roja, blanca
        y azul de Medicare)
      </li>
      <li>Su dirección permanente y su número de teléfono</li>
    </ul>
    <p>
      <strong>Nota:</strong> Se deben completar todos los puntos de la paso 1
      (Personal). Los puntos de la paso 3 (Opciones) son opcionales: no pueden
      negarle la cobertura por no completarlos.
    </p>
    <h2>Para recordar</h2>
    <ul>
      <li>
        Si desea unirse a un plan durante el Período de inscripción abierta de
        otoño (del 15 de octubre al 7 de diciembre), el plan deberá recibir el
        formulario completo, a más tardar, el 7 de diciembre.
      </li>
      <li>
        Si debe pagar una prima del plan, su plan le enviará una factura. Usted
        puede optar por suscribirse para que el pago de las primas se deduzca de
        su beneficio mensual del Seguro Social (o de la Junta de Jubilación de
        Empleados Ferroviarios).
      </li>
    </ul>
    <h2>¿Qué debo hacer después?</h2>
    <p>
      Una vez que procesen la solicitud de inscripción, se comunicarán con
      usted.
    </p>
    <h2>¿A quién puedo llamar si necesito ayuda con este formulario?</h2>
    <p>
      Llame a Devoted Health Plans al{' '}
      <a href="tel:1-800-990-0723">1-800-990-0723</a> (TTY 711). O bien,
      comuníquese con Medicare al 1-800-MEDICARE (1-800-633-4227). Los usuarios
      de TTY pueden llamar al 1-877-486-2048.
    </p>
    <h2>Personas en situación de calle:</h2>
    <p>
      Si desea unirse a un plan, pero no tiene una residencia permanente,
      podemos considerar un apartado postal, la dirección de un refugio o una
      clínica, o la dirección donde recibe su correspondencia (p. ej., cheques
      del Seguro Social) como su dirección de residencia permanente.{' '}
    </p>
  </>
);
export default Body;
